import is from '@sindresorhus/is';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { AgentChatMessage, FrontendContext, UserMessage } from '../../data-contract/chat';
import { chatKeys, useChatThread as useChatThreadQuery } from '../api';
import {
    appendStructuredEvent,
    appendTextChunk,
    type MessagesInfiniteData,
    prependUserMessage,
    setAssistantMessageStatus,
    upsertAssistantMessage,
} from '../messages-cache';
import { useChatStream } from './use-chat-stream';
import { useChatThread } from './use-chat-thread';

export type TurnStatus = 'complete' | 'error' | 'idle' | 'streaming';

export type ChatTurnState = {
    readonly status: TurnStatus;
    readonly messageId: string | null;
    readonly errorMessage: string | null;
};

export type SendOptions = {
    readonly message: string;
    readonly frontendContext?: FrontendContext;
};

export type ChatConversation = {
    readonly clearTurn: () => void;
    readonly isFreshThread: boolean;
    readonly isCreatingThread: boolean;
    readonly resumeTurn: () => Promise<void>;
    readonly send: (options: SendOptions) => Promise<void>;
    readonly stop: () => void;
    readonly threadId: string | null;
    readonly turn: ChatTurnState;
};

const INITIAL_TURN: ChatTurnState = { errorMessage: null, messageId: null, status: 'idle' };

const generateId = (): string =>
    is.function(crypto.randomUUID) ? crypto.randomUUID() : `local-${Date.now()}-${Math.random()}`;

const buildSendBody = (message: string, frontendContext: FrontendContext | undefined): UserMessage =>
    is.undefined(frontendContext) ? { message, type: 'new' } : { context: frontendContext, message, type: 'new' };

export const useChatConversation = (): ChatConversation => {
    const queryClient = useQueryClient();
    const { ensureThreadId, isCreatingThread, isFreshThread, threadId } = useChatThread();
    const [turn, setTurn] = useState<ChatTurnState>(INITIAL_TURN);

    const streamThreadIdRef = useRef<string | null>(null);
    const currentMessageIdRef = useRef<string | null>(null);

    const threadQuery = useChatThreadQuery(threadId ?? undefined);
    const pendingTurn = threadQuery.data?.pendingTurn;

    const updateMessages = useCallback(
        (
            streamThreadId: string,
            updater: (data: MessagesInfiniteData | undefined) => MessagesInfiniteData | undefined,
        ): void => {
            queryClient.setQueriesData<MessagesInfiniteData>({ queryKey: chatKeys.messages(streamThreadId) }, updater);
        },
        [queryClient],
    );

    const stream = useChatStream({
        onChunk: (text) => {
            const tid = streamThreadIdRef.current;
            const messageId = currentMessageIdRef.current;
            if (is.null(tid) || is.null(messageId)) return;
            updateMessages(tid, (data) => appendTextChunk(data, messageId, text));
        },
        onComplete: () => {
            const tid = streamThreadIdRef.current;
            const messageId = currentMessageIdRef.current;
            if (!is.null(tid) && !is.null(messageId)) {
                updateMessages(tid, (data) => setAssistantMessageStatus(data, messageId, 'complete'));
            }
            setTurn((prev) => ({ ...prev, status: 'complete' }));
        },
        onConnectionLost: () => {
            const tid = streamThreadIdRef.current;
            const messageId = currentMessageIdRef.current;
            if (!is.null(tid) && !is.null(messageId)) {
                updateMessages(tid, (data) => setAssistantMessageStatus(data, messageId, 'error'));
            }
            setTurn((prev) => ({ ...prev, errorMessage: prev.errorMessage ?? 'Connection lost', status: 'error' }));
        },
        onError: (event) => {
            const tid = streamThreadIdRef.current;
            const messageId = currentMessageIdRef.current;
            if (!is.null(tid) && !is.null(messageId)) {
                updateMessages(tid, (data) => setAssistantMessageStatus(data, messageId, 'error'));
            }
            setTurn((prev) => ({ ...prev, errorMessage: event.payload.message, status: 'error' }));
        },
        onMessageStart: (event) => {
            const tid = streamThreadIdRef.current;
            currentMessageIdRef.current = event.messageId;
            if (!is.null(tid)) {
                updateMessages(tid, (data) => upsertAssistantMessage(data, event.messageId, event.timestamp));
            }
            setTurn((prev) => ({ ...prev, messageId: event.messageId }));
        },
        onStructuredResponse: (event) => {
            const tid = streamThreadIdRef.current;
            const messageId = currentMessageIdRef.current;
            if (is.null(tid) || is.null(messageId)) return;
            updateMessages(tid, (data) => appendStructuredEvent(data, messageId, event));
        },
    });

    const startTurn = useCallback(
        (streamThreadId: string, body: UserMessage): void => {
            streamThreadIdRef.current = streamThreadId;
            currentMessageIdRef.current = null;
            setTurn({ errorMessage: null, messageId: null, status: 'streaming' });
            stream.connect(streamThreadId, body);
        },
        [stream],
    );

    const send = useCallback(
        async ({ frontendContext, message }: SendOptions): Promise<void> => {
            const trimmed = message.trim();
            if (trimmed.length === 0) return;

            const tid = await ensureThreadId();
            const userMessage: AgentChatMessage = {
                createdAt: new Date().toISOString(),
                id: generateId(),
                role: 'user',
                text: trimmed,
            };
            updateMessages(tid, (data) => prependUserMessage(data, userMessage));
            startTurn(tid, buildSendBody(trimmed, frontendContext));
        },
        [ensureThreadId, startTurn, updateMessages],
    );

    const resumeTurn = useCallback(async (): Promise<void> => {
        if (is.null(threadId)) return;
        startTurn(threadId, { resume: true });
    }, [threadId, startTurn]);

    const stop = useCallback((): void => {
        stream.disconnect();
        setTurn(INITIAL_TURN);
    }, [stream]);

    const clearTurn = useCallback((): void => setTurn(INITIAL_TURN), []);

    const hasResumedRef = useRef(false);
    useEffect(() => {
        if (is.null(threadId) || is.nullOrUndefined(pendingTurn)) return;
        if (turn.status === 'streaming') return;
        if (hasResumedRef.current) return;
        hasResumedRef.current = true;
        void resumeTurn();
    }, [threadId, pendingTurn, turn.status, resumeTurn]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: resets the resume-once latch whenever the active thread changes.
    useEffect(() => {
        hasResumedRef.current = false;
    }, [threadId]);

    return {
        clearTurn,
        isCreatingThread,
        isFreshThread,
        resumeTurn,
        send,
        stop,
        threadId,
        turn,
    };
};
